import { GridApi } from 'ag-grid-community';
import React, { createContext, useCallback, useContext, useState } from 'react';

import {
  TDataContext,
  TDataItem,
  TDataTableProvider,
} from '@/shared/components/ui/data-table/context/data-table-context.types';
import { useApplySortFilter } from '@/shared/components/ui/data-table/hooks/use-apply-sort-filter/use-apply-sort-filter';
import { useBackgroundRefetch } from '@/shared/hooks/use-background-refetch/use-background-refetch';

const DataTableContext = createContext<TDataContext<any> | undefined>(
  undefined,
);

export const useDataTable = <T,>(): TDataContext<T> => {
  const context = useContext(DataTableContext);
  if (!context) {
    throw new Error('useDataTable must be used within DataTableProvider');
  }
  return context;
};

export const DataTableProvider = <T extends Partial<TDataItem>>({
  children,
  value,
  colDefs,
  onGridReady,
}: TDataTableProvider<T>) => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const { items, isFetching } = value.data;
  const [gridApi, setGridApi] = useState<GridApi | undefined>();

  const visibleIsFetching = useBackgroundRefetch(isFetching);

  const setGridApiCallback = useCallback(
    (api: GridApi) => {
      setGridApi(api);
      onGridReady?.(api);
    },
    [onGridReady],
  );

  value.data.items = useApplySortFilter(items);

  const selectableItems = (value.data.items ?? []).filter(
    (item) => !value.disableItemCheckbox?.(item),
  );

  const contextValue: TDataContext<T> = {
    selectedItems,
    setSelectedItems,
    selectableItems,
    colDefs,
    gridApi,
    setGridApi: setGridApiCallback,
    ...value,
    data: {
      ...value.data,
      isFetching: visibleIsFetching,
    },
  };

  return <DataTableContext value={contextValue}>{children}</DataTableContext>;
};
